import React, { useRef, useState } from 'react';

const DRAG_THRESHOLD = 8;

const loadSavedPos = (key) => {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    if (
      saved &&
      Number.isFinite(saved.x) && Number.isFinite(saved.y) &&
      saved.x >= -40 && saved.y >= 0 &&
      saved.x <= window.innerWidth - 60 &&
      saved.y <= window.innerHeight - 40
    ) {
      return saved;
    }
  } catch {}
  return null;
};

/* Fixed-position container whose position the user can drag via the grip
   handle. Dragging by the whole box would fight with the form inputs inside
   it, so only the grip starts a drag. Position persists via localStorage. */
const DraggableBox = ({ storageKey, className = '', children }) => {
  const [pos, setPos] = useState(() => loadSavedPos(storageKey));
  const boxRef = useRef(null);
  const dragRef = useRef(null);

  const clamp = (x, y) => {
    const rect = boxRef.current ? boxRef.current.getBoundingClientRect() : null;
    const w = rect ? rect.width : 240;
    const h = rect ? rect.height : 70;
    return {
      x: Math.min(Math.max(x, 0), Math.max(0, window.innerWidth - w)),
      y: Math.min(Math.max(y, 0), Math.max(0, window.innerHeight - h)),
    };
  };

  const handlePointerDown = (e) => {
    // Never hijack touches meant for the form controls inside the box -
    // everything else (grip, headings, badges, empty areas) drags the box.
    if (e.target.closest('input, select, button, label, a, textarea')) return;
    e.preventDefault();
    const rect = boxRef.current.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
      lastPos: null,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (!d.moved) {
      if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < DRAG_THRESHOLD) return;
      d.moved = true;
    }
    const next = clamp(e.clientX - d.offsetX, e.clientY - d.offsetY);
    d.lastPos = next;
    setPos(next);
  };

  const handlePointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || !d.moved || !d.lastPos) return;
    try { localStorage.setItem(storageKey, JSON.stringify(d.lastPos)); } catch {}
  };

  const style = pos
    ? {
        position: 'fixed', left: pos.x, top: pos.y,
        right: 'auto', bottom: 'auto',
        touchAction: 'none',
      }
    : { touchAction: 'none' };

  return (
    <div
      ref={boxRef}
      className={className}
      style={style}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => { dragRef.current = null; }}
    >
      <span className="order-box-grip" title="Drag to move">
        ⋯
      </span>
      {children}
    </div>
  );
};

export default DraggableBox;
